/**
 * The brand's affiliate program as the Acquisition page and Home read it: the affiliate count for the Self-managed
 * Affiliates card, and the steps behind the affiliate Acquisition score (`affiliate-score.ts`).
 *
 * The status probe never provisions. The affiliate list, the invites and the affiliate campaigns are read only once
 * the owner is provisioned (reading them would otherwise provision external records just from viewing a page). The
 * score's own reads (invites, campaigns, and the last 30 days of affiliate registrations and first deposits from
 * Stats, the Home Affiliates card's query) run only while `withScore`. A read that fails counts its step as not taken.
 */
import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchStatsDataSetHistory, statsHistoryKey, statsQueryKey } from '@/store/slices/ownerHubStats';

import { fetchStatus, listAffiliates, listCampaigns, listInvites } from '../affiliates/api';
import { useAffiliateResource } from '../affiliates/hooks/useAffiliateResource';
import { HOME_CHANNEL_STATS, homeWindow } from '../home/stats-mapping';
import { sumBuckets, toBucketsFor } from '../home/stats-shape';
import { useBrandSettled } from '../website/useBrandSettled';
import { affiliateAcquisitionScore, type AffiliateScore } from './affiliate-score';

export type AffiliateProgress = {
  /** The owner's affiliates: undefined while the status probe resolves, 0 when not provisioned. */
  affiliateCount: number | undefined;
  /** The affiliate Acquisition score (meaningful only with `withScore`). */
  score: AffiliateScore;
  /** True until every read the score needs has answered. */
  scoreLoading: boolean;
};

const { dataSet, aggregateOn, fields } = HOME_CHANNEL_STATS;
/** The `aft` the affiliate program's players arrive under (the Home Affiliates card's). */
const AFFILIATE_AFT = 'affiliates';
const AFFILIATE_FILTER: Record<string, string> = { aft: AFFILIATE_AFT };

/**
 * @param casinoInstanceId Active brand id (`displayBrand?.id`); undefined keeps every read idle.
 * @param withScore Also read what the score needs (outside the "777" reveal).
 */
export const useAffiliateProgress = (casinoInstanceId: string | undefined, withScore: boolean): AffiliateProgress => {
  const dispatch = useAppDispatch();
  const brandSettled = useBrandSettled();

  const status = useAffiliateResource(casinoInstanceId, fetchStatus);
  const provisioned = status.data?.enabled === true && !!status.data.user;
  const affiliates = useAffiliateResource(provisioned ? casinoInstanceId : undefined, listAffiliates);
  const scoreListsId = withScore && provisioned ? casinoInstanceId : undefined;
  const invites = useAffiliateResource(scoreListsId, listInvites);
  const campaigns = useAffiliateResource(scoreListsId, listCampaigns);

  // The last 30 days, fixed for the page's life: the steps only ask whether anything came through.
  const window = React.useMemo(() => homeWindow('30D', Date.now()), []);
  const statsId = withScore ? casinoInstanceId : undefined;
  // Syncs with the network: the affiliate players' registrations and first deposits over the window.
  React.useEffect(() => {
    if (!statsId) return;
    void dispatch(
      fetchStatsDataSetHistory({
        casinoInstanceId: statsId,
        dataSet,
        startDate: window.startDate,
        endDate: window.endDate,
        intervalSlice: window.intervalSlice,
        aggregateOn: [...aggregateOn],
        filter: AFFILIATE_FILTER,
      }),
    );
  }, [dispatch, statsId, window]);
  const statsKey = statsId
    ? statsHistoryKey(statsId, dataSet, window.startDate, window.endDate, window.intervalSlice, statsQueryKey(aggregateOn, AFFILIATE_FILTER))
    : undefined;
  const statsEntry = useAppSelector((s) => (statsKey ? s.ownerHubStats.historyByKey[statsKey] : undefined));
  const statsLoadingFlag = useAppSelector((s) => (statsKey ? !!s.ownerHubStats.historyLoadingByKey[statsKey] : false));
  const statsError = useAppSelector((s) => (statsKey ? s.ownerHubStats.historyErrorByKey[statsKey] : undefined));

  const buckets = statsEntry ? toBucketsFor(statsEntry.data, aggregateOn[0], AFFILIATE_AFT) : [];
  const score = affiliateAcquisitionScore({
    'set-up': provisioned,
    invite:
      (affiliates.data?.length ?? 0) > 0 || (invites.data ?? []).some((invite) => invite.status !== 'revoked'),
    campaign: (campaigns.data?.length ?? 0) > 0,
    registration: sumBuckets(buckets, fields.registrations) > 0,
    ftd: sumBuckets(buckets, fields.ftds) > 0,
  });

  const statsPending = statsEntry === undefined && (statsLoadingFlag || statsError === undefined);
  const listsPending = provisioned && (affiliates.loading || invites.loading || campaigns.loading);
  // No brand id: loading until the brand list settles (a settled brand without one reads as no step taken).
  const scoreLoading = casinoInstanceId ? status.loading || listsPending || statsPending : !brandSettled;

  return {
    affiliateCount: status.loading ? undefined : provisioned ? affiliates.data?.length : 0,
    score,
    scoreLoading,
  };
};

export default useAffiliateProgress;
